'use client';

import { PlanConfirmationModal } from 'components';
import { UpgradePlanContainerProps, PlanPeriod, ActionResult, PlanData } from 'components';
import { JSX, useCallback, useState } from 'react';
import { SubscriptionStatus } from 'subscription-service-client';

import StatusMessage from '../../../../components/StatusMessage';
import { PlanCard } from './PlanCard/PlanCard';
import { PlanToggle } from './PlanToggle';
import { UpgradePlanHeader } from './UpgradePlanHeader';

export const UpgradePlanContainer = ({
    plans,
    defaultPeriod,
    currencySymbol,
    currencyCode,
    subscriptionId,
    billingInfo,
    currentPlan,
    currentBillingAmount,
    redirectAfterRegister,
    subscriptionData,
}: UpgradePlanContainerProps): JSX.Element => {
    const [selectedPeriod, setSelectedPeriod] = useState<PlanPeriod>(defaultPeriod);
    const [statusMessage, setStatusMessage] = useState<ActionResult | null>(null);
    const [upgradedPlan, setUpgradedPlan] = useState<{ planId: string; period: PlanPeriod } | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState<PlanData | null>(null);

    const handleStatusUpdate = useCallback(
        (result: ActionResult, planId: string) => {
            setStatusMessage(result);
            // Only set upgradedPlan if the change was activated (immediate upgrades)
            // Downgrades with future activation dates won't show as "upgraded"
            if (result.success && result.activated) {
                setUpgradedPlan({ planId, period: selectedPeriod });

                // If redirectAfterRegister is provided, redirect the user
                if (redirectAfterRegister) {
                    // Use window.location.href for client-side redirect
                    window.location.href = `/${redirectAfterRegister}`;
                }
            }
        },
        [selectedPeriod, redirectAfterRegister],
    );

    const handleDismissMessage = useCallback(() => {
        setStatusMessage(null);
    }, []);

    const handleOpenModal = useCallback((plan: PlanData) => {
        setSelectedPlanForUpgrade(plan);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedPlanForUpgrade(null);
    }, []);

    // Check if subscription is cancelled or expired
    const isCancelledOrExpired =
        subscriptionData?.status === SubscriptionStatus.Canceled || subscriptionData?.status === 'expired';

    return (
        <div className="bg-blue-100 min-h-0 flex-1 flex flex-col gap-6 md:gap-10 items-center justify-start px-4 md:px-6 lg:px-8 py-6 md:py-12">
            <div className="flex flex-col gap-4 items-start justify-start w-full">
                <UpgradePlanHeader />

                <div className="flex flex-col gap-2.5 items-center justify-start w-full">
                    <PlanToggle selectedPeriod={selectedPeriod} onPeriodChange={setSelectedPeriod} />
                </div>
            </div>

            {statusMessage && (
                <div className="w-full max-w-4xl px-2 md:px-0">
                    <StatusMessage
                        message={statusMessage.message}
                        success={statusMessage.success}
                        onDismiss={handleDismissMessage}
                        className="mb-4"
                    />
                </div>
            )}

            <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-stretch md:items-start justify-center w-full max-w-6xl">
                {plans.map((plan) => {
                    let updatedPlan = plan;

                    if (upgradedPlan) {
                        if (upgradedPlan.planId === plan.id) {
                            updatedPlan = {
                                ...plan,
                                currentUserPlan: {
                                    isCurrentMonthly: upgradedPlan.period === 'monthly',
                                    isCurrentAnnual: upgradedPlan.period === 'annual',
                                },
                            };
                        } else {
                            updatedPlan = {
                                ...plan,
                                currentUserPlan: {
                                    isCurrentMonthly: false,
                                    isCurrentAnnual: false,
                                },
                            };
                        }
                    }

                    return (
                        <PlanCard
                            key={plan.id}
                            plan={updatedPlan}
                            selectedPeriod={selectedPeriod}
                            currencySymbol={currencySymbol}
                            subscriptionId={subscriptionId}
                            isCancelledOrExpired={isCancelledOrExpired}
                            onStatusUpdate={(result) => handleStatusUpdate(result, plan.id)}
                            onOpenModal={() => handleOpenModal(updatedPlan)}
                        />
                    );
                })}
            </div>

            <PlanConfirmationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                plan={selectedPlanForUpgrade}
                selectedPeriod={selectedPeriod}
                currencySymbol={currencySymbol}
                currencyCode={currencyCode}
                subscriptionId={subscriptionId}
                billingInfo={billingInfo}
                currentBillingAmount={currentBillingAmount}
                subscriptionData={subscriptionData}
                onStatusUpdate={(result) => {
                    handleStatusUpdate(result, selectedPlanForUpgrade?.id || '');
                }}
            />
        </div>
    );
};
